import { FC, useEffect, useState } from 'react'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
	TouchSensor,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { ExerciseParams } from '@pages/TrainingConstructor/types'
import { SortableExerciseItem } from './sortable'
import { cn } from '@/lib/utils'

const getExerciseItemId = (params: ExerciseParams): number | string =>
	params.instanceId ?? params.exercise_id

interface DraggableExercisesListProps {
	exerciseParams: ExerciseParams[]
	setExerciseParams: (params: ExerciseParams[]) => void
	addedExercises: ExerciseParams[]
	onDelete: (id: number) => void
	onAddExercise?: () => void
	className?: string
	isReadonly?: boolean
	expandReadonlyByDefault?: boolean
	readonlySummaryLabel?: string
}

export const ExercisesAccordion: FC<DraggableExercisesListProps> = ({
	exerciseParams,
	setExerciseParams,
	addedExercises,
	onDelete,
	onAddExercise,
	className,
	isReadonly = false,
	expandReadonlyByDefault = true,
	readonlySummaryLabel,
}) => {
	const [expandedExerciseIds, setExpandedExerciseIds] = useState<Set<number | string>>(
		() =>
			new Set(
				isReadonly && expandReadonlyByDefault
					? exerciseParams.map(getExerciseItemId)
					: [],
			),
	)
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
		useSensor(KeyboardSensor),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 100,
				tolerance: 5,
			},
		})
	)

	const handleDragEnd = (event: DragEndEvent) => {
		if (isReadonly) {
			return
		}

		const { active, over } = event

		if (active.id !== over?.id && over) {
			const oldIndex = exerciseParams.findIndex(p => getExerciseItemId(p) === active.id)
			const newIndex = exerciseParams.findIndex(p => getExerciseItemId(p) === over.id)

			const newParams = arrayMove(exerciseParams, oldIndex, newIndex)
			const updatedParams = newParams.map((param, index) => ({
				...param,
				order: index + 1,
			}))

			setExerciseParams(updatedParams)
		}
	}

	const handleParamsChange = (index: number, newParams: ExerciseParams) => {
		const updated = [...exerciseParams]
		updated[index] = newParams
		setExerciseParams(updated)
	}

	const handleDelete = (id: number) => {
		setExpandedExerciseIds((current) => {
			const next = new Set(current)
			next.delete(id)
			return next
		})

		onDelete(id)
	}

	useEffect(() => {
		const availableIds = new Set(exerciseParams.map(getExerciseItemId))

		setExpandedExerciseIds((current) => {
			const next = new Set<number | string>()

			if (isReadonly && expandReadonlyByDefault) {
				availableIds.forEach((id) => next.add(id))
			} else {
				current.forEach((id) => {
					if (availableIds.has(id)) {
						next.add(id)
					}
				})
			}

			return next
		})
	}, [exerciseParams, expandReadonlyByDefault, isReadonly])

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			modifiers={[restrictToVerticalAxis]}>
			<SortableContext
				items={exerciseParams.map(getExerciseItemId)}
				strategy={verticalListSortingStrategy}>
				<div className={cn('training-modal-exercises-list', className)}>
					{exerciseParams.map((params, index) => {
						const itemId = getExerciseItemId(params)
						const isExpanded = expandedExerciseIds.has(itemId)
						const exercise = addedExercises.find(
							exercise =>
								getExerciseItemId(exercise) === itemId ||
								exercise.exercise_id === params.exercise_id
						)

						if (!exercise) return null

						return (
							<SortableExerciseItem
								onDelete={handleDelete}
								key={`${itemId}-${isExpanded ? 'expanded' : 'collapsed'}`}
								id={itemId}
								params={params}
								onParamsChange={newParams => handleParamsChange(index, newParams)}
								isExpanded={isExpanded}
								isReadonly={isReadonly}
								readonlySummaryLabel={readonlySummaryLabel}
								onExpandedChange={(isExpanded) => {
									setExpandedExerciseIds((current) => {
										if (!isReadonly) {
											return isExpanded ? new Set([itemId]) : new Set()
										}

										const next = new Set(current)

										if (isExpanded) {
											next.add(itemId)
										} else {
											next.delete(itemId)
										}

										return next
									})
								}}
							/>
						)
					})}
					{!isReadonly && (
						<button
							type="button"
							className="training-modal-add-exercise"
							onClick={onAddExercise}
						>
							+ Добавить упражнение
						</button>
					)}
				</div>
			</SortableContext>
		</DndContext>
	)
}
